import {
  AHSeparator,
  AHText,
  AHWidgetTitle,
  Box,
  Circle,
  ListItem,
  PublicOrPrivateImage,
  TouchableBox,
  UserCircle,
  useWidgetOptions,
} from "arkh";
import React from "react";
import { graphql, useFragment } from "react-relay";

import { WidgetEvent } from "../WidgetEventT";
import { WidgetEmployeeLeave_widgetEmployeeLeave$key } from "./__generated__/WidgetEmployeeLeave_widgetEmployeeLeave.graphql";
import { FlatList } from "react-native";
import { useSetWidgetFocus } from "../useSetFocus";
import {
  WidgetEmployeeLeaveItem_item$key,
  WidgetEmployeeLeaveItem_item$data,
} from "./__generated__/WidgetEmployeeLeaveItem_item.graphql";

function getSingleLeaveReason(item: WidgetEmployeeLeaveItem_item$data) {
  const firstShiftLeaveReason = item.shifts[0].leaveReason;
  const firstCode = firstShiftLeaveReason?.id;
  return firstCode !== null &&
    firstCode !== undefined &&
    item.shifts.every((obj) => obj.leaveReason?.id === firstCode)
    ? firstShiftLeaveReason
    : null;
}

function WidgetEmployeeLeaveItem(props: {
  item: WidgetEmployeeLeaveItem_item$key;
}) {
  const item = useFragment(
    graphql`
      fragment WidgetEmployeeLeaveItem_item on WidgetEmployeeLeaveItem {
        id
        employee {
          id
          name
          image {
            ...ImagePublicOrPrivate_image
          }
        }
        shifts {
          id
          startAt
          endAt
          formattedStartAtTime
          formattedEndAtTime
          leaveReason {
            id
            leaveReason
            leaveCode
          }
        }
      }
    `,
    props.item
  );

  const singleLeaveReason = getSingleLeaveReason(item);

  return (
    <Box
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
      }}
      space="small"
    >
      <Box style={{ flexDirection: "row", alignItems: "center" }}>
        <Box spaceRight="small">
          {item.employee.image ? (
            <Circle
              size="smallMedium"
              color="surface"
              borderColor="border-primary"
              borderWidth={1}
              style={{
                overflow: "hidden",
              }}
            >
              <PublicOrPrivateImage
                pointerEvents="none"
                image={item.employee.image}
                style={{
                  width: "100%",
                  height: "100%",
                  ...{ pointerEvents: "none" },
                }}
              />
            </Circle>
          ) : (
            <UserCircle name={item.employee.name} />
          )}
        </Box>
        <ListItem.Title>{item.employee.name}</ListItem.Title>
      </Box>
      <Box style={{ flexDirection: "column" }}>
        {singleLeaveReason ? (
          <AHText spaceHorizontal="small">
            {singleLeaveReason.leaveReason}
          </AHText>
        ) : (
          item.shifts.map((shift) =>
            shift.leaveReason === null ? null : (
              <Box
                key={shift.id}
                style={{ flexDirection: "row" }}
                spaceHorizontal="small"
              >
                <AHText spaceHorizontal="small">
                  {shift.leaveReason.leaveReason}
                </AHText>
                <AHText>
                  {`${shift.formattedStartAtTime} - ${shift.formattedEndAtTime}`}
                </AHText>
              </Box>
            )
          )
        )}
      </Box>
    </Box>
  );
}

export const WidgetEmployeeLeave = (props: {
  widget: WidgetEmployeeLeave_widgetEmployeeLeave$key;
  storecastId?: string;
  mode?: "FOCUS" | "IDLE";
  onEvent?: (event: WidgetEvent) => void;
  interactive?: undefined | false;
}) => {
  const widget = useFragment(
    graphql`
      fragment WidgetEmployeeLeave_widgetEmployeeLeave on WidgetEmployeeLeave {
        id
        title
        employeesOnLeave {
          id
          ...WidgetEmployeeLeaveItem_item
        }
      }
    `,
    props.widget
  );

  const focus = useSetWidgetFocus(widget.id, props.storecastId);
  const { background } = useWidgetOptions();

  return (
    <TouchableBox
      color="surface-primary"
      disabled={props.mode !== "FOCUS"}
      onPress={() => focus()}
      style={{
        flex: 1,
        backgroundColor: background,
      }}
    >
      <AHWidgetTitle>{widget.title}</AHWidgetTitle>
      <FlatList
        scrollEnabled={props.interactive !== false}
        showsVerticalScrollIndicator={false}
        keyExtractor={({ id }) => id}
        data={widget.employeesOnLeave}
        renderItem={({ item }) => <WidgetEmployeeLeaveItem item={item} />}
        ItemSeparatorComponent={AHSeparator}
      />
    </TouchableBox>
  );
};
